import { Auth } from 'aws-amplify';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';

export enum FeatureFlag {
  AI_ASSISTANT = 'ai_assistant',
  ORION_UI = 'orion_ui',
}

export type FeatureFlagMap = Record<FeatureFlag, boolean>;

const LAMBDA_ARN = 'arn:aws:lambda:us-east-1:266735818235:function:ControllershipHub-FeatureFlagHandler-prod:live';
const REGION = 'us-east-1';

/** Default values — all flags disabled for safety */
export const defaultFlags: FeatureFlagMap = {
  [FeatureFlag.AI_ASSISTANT]: false,
  [FeatureFlag.ORION_UI]: false,
};

const STORAGE_KEY = 'chub.featureFlags.v1';
// Cache TTL: 1 hour. Long enough to eliminate flash-of-old-UI on common
// reloads, short enough to pick up flag flips reasonably soon. The cache is
// also overwritten on every successful fetch, so the staleness window is
// bounded.
const CACHE_TTL_MS = 60 * 60 * 1000;

interface PersistedCache {
  flags: FeatureFlagMap;
  expiresAt: number;
}

let cache: FeatureFlagMap | null = null;

/**
 * Reads a previously persisted flags snapshot from localStorage if present
 * and not expired. Used by `useOrionUI` (and any other consumer that wants
 * instant first paint) so we render the correct UI on the very first frame
 * after a reload, instead of flashing the default-off Cloudscape UI for
 * 200-300ms while the Lambda call resolves. Returns `null` if there's no
 * usable persisted snapshot.
 *
 * IMPORTANT: this function deliberately does NOT hydrate the module-level
 * `cache` variable. If it did, `fetchFeatureFlags()` would short-circuit on
 * its first call and never hit the Lambda — which means newly flipped flag
 * values wouldn't propagate until the localStorage TTL expired (1 hour).
 * The persisted value is for first-paint only; the Lambda call is still
 * the source of truth for the current session.
 */
export const getCachedFeatureFlags = (): FeatureFlagMap | null => {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedCache;
    if (!parsed || typeof parsed.expiresAt !== 'number') return null;
    if (Date.now() > parsed.expiresAt) return null;
    // Return persisted flags WITHOUT hydrating the module-level cache, so
    // fetchFeatureFlags() still calls the Lambda on its next invocation
    // and picks up any newly-flipped flag values for this session.
    return { ...defaultFlags, ...parsed.flags };
  } catch {
    // Treat any read/parse failure (corrupt JSON, schema mismatch,
    // localStorage unavailable in incognito, etc.) as "no cache" — the async
    // fetch path will fill the cache on success. Intentionally swallowed: the
    // cache is an optimization, never a source of truth.
    return null;
  }
};

const persistFlags = (flags: FeatureFlagMap) => {
  try {
    const payload: PersistedCache = { flags, expiresAt: Date.now() + CACHE_TTL_MS };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage may be full or disabled (incognito). Ignore — in-memory cache
    // still works for the rest of the session.
  }
};

/**
 * Fetches feature flags from the FeatureFlagHandler Lambda via AppConfig.
 * On localhost or if Lambda ARN is not configured, enables all flags.
 * Caches results for the session and persists to localStorage so the next
 * page load can render with the correct flag values on the first frame.
 */
export const fetchFeatureFlags = async (): Promise<FeatureFlagMap> => {
  if (cache) return cache;

  // Localhost: enable all flags for local development
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalhost) {
    console.log('[FeatureFlags] Localhost detected, enabling all flags');
    cache = Object.fromEntries(
      Object.values(FeatureFlag).map((flag) => [flag, true])
    ) as FeatureFlagMap;
    persistFlags(cache);
    return cache;
  }

  // Runtime check - prevent webpack from optimizing this away
  const lambdaArn = String(LAMBDA_ARN);
  if (!lambdaArn || lambdaArn.includes('{{')) {
    console.log('[FeatureFlags] Lambda ARN not configured, disabling all flags');
    cache = defaultFlags;
    return cache;
  }

  try {
    console.log('[FeatureFlags] Calling Lambda...', lambdaArn);
    const credentials = await Auth.currentCredentials();
    const client = new LambdaClient({ region: String(REGION), credentials });

    const response = await client.send(
      new InvokeCommand({
        FunctionName: lambdaArn,
        Payload: new TextEncoder().encode(JSON.stringify({})),
      })
    );

    const responseBody = JSON.parse(new TextDecoder().decode(response.Payload));
    const flags: FeatureFlagMap = { ...defaultFlags };

    for (const flag of Object.values(FeatureFlag)) {
      if (responseBody.body?.[flag]?.enabled !== undefined) {
        flags[flag] = responseBody.body[flag].enabled;
      }
    }

    cache = flags;
    persistFlags(flags);
    return cache;
  } catch (error) {
    console.error('[FeatureFlags] Error fetching flags:', error);
    cache = defaultFlags;
    return cache;
  }
};

export const isFeatureEnabled = (flags: FeatureFlagMap, flag: FeatureFlag): boolean => {
  return flags[flag] ?? false;
};
